import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Coins, 
  TrendingUp, 
  Gift, 
  History, 
  Plus, 
  Crown, 
  Star,
  Users,
  Share2,
  Calendar,
  Target,
  Zap
} from 'lucide-react'
import { useRraasiCoins } from '../hooks/useRraasiCoins'
import { useSubscriptionStore } from '../stores/subscriptionStore'
import RraasiCoinDisplay from '../components/RraasiCoinDisplay'

const RraasiCoins: React.FC = () => {
  const { 
    balance, 
    transactions, 
    addBonusCoins, 
    getCoinStats, 
    getRecentTransactions,
    isHeavySpender,
    getEarningSuggestions
  } = useRraasiCoins()
  
  const { isSubscriptionActive, getActivePlan } = useSubscriptionStore()
  const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'earn' | 'features'>('overview')
  
  const activePlan = getActivePlan()
  const stats = getCoinStats()
  const recentTransactions = getRecentTransactions(10)
  const earningSuggestions = getEarningSuggestions()

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Coins },
    { id: 'history', label: 'History', icon: History },
    { id: 'earn', label: 'Earn Coins', icon: TrendingUp },
    { id: 'features', label: 'Features', icon: Target }
  ]

  const earningMethods = [
    {
      id: 'daily_login',
      title: 'Daily Login',
      description: 'Log in every day to earn coins',
      coins: 5,
      icon: Calendar,
      action: () => addBonusCoins(5, 'Daily login bonus!'),
      available: true
    },
    {
      id: 'referral',
      title: 'Refer Friends',
      description: 'Get friends to join Rraasi',
      coins: 25,
      icon: Users,
      action: () => addBonusCoins(25, 'Referral bonus!'),
      available: true
    },
    {
      id: 'social_share',
      title: 'Social Sharing',
      description: 'Share your astrological insights',
      coins: 10,
      icon: Share2,
      action: () => addBonusCoins(10, 'Social sharing bonus!'),
      available: true
    },
    {
      id: 'subscription',
      title: 'Upgrade Subscription',
      description: 'Get bonus coins with premium plans',
      coins: activePlan?.rraasiCoins || 0,
      icon: Crown,
      action: () => {},
      available: !isSubscriptionActive(),
      highlight: true
    }
  ]

  const featureCategories = [
    {
      category: 'Chat & Consultation',
      features: [
        { name: 'Basic Chat', cost: 5, description: 'General astrology questions' },
        { name: 'Advanced Chat', cost: 10, description: 'Detailed personal readings' },
        { name: 'Expert Consultation', cost: 25, description: 'Professional guidance' }
      ]
    },
    {
      category: 'Analysis & Reports',
      features: [
        { name: 'Birth Chart', cost: 15, description: 'Detailed natal chart analysis' },
        { name: 'Compatibility Check', cost: 20, description: 'Relationship analysis' },
        { name: 'Custom Report', cost: 50, description: 'Personalized detailed report' }
      ]
    },
    {
      category: 'Predictions & Insights',
      features: [
        { name: 'Daily Horoscope', cost: 3, description: 'Personalized daily predictions' },
        { name: 'Weekly Forecast', cost: 8, description: 'Weekly insights and planning' },
        { name: 'Life Path Reading', cost: 75, description: 'Comprehensive life analysis' }
      ]
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 to-blue-900 border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <Coins className="w-10 h-10 text-white" />
            </motion.div>
            <h1 className="text-4xl font-bold mb-2">RRAASI Coins</h1>
            <p className="text-xl text-gray-300">Your cosmic currency for unlocking premium features</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* Tab Content */}
        <div className="space-y-8">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Main Coin Display */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <RraasiCoinDisplay showHistory={false} />
                
                {/* Quick Stats */}
                <div className="bg-white/10 rounded-2xl p-6 border border-white/20">
                  <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    Coin Statistics
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Total Earned</span>
                      <span className="text-green-400 font-semibold">+{stats.totalEarned.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Total Spent</span>
                      <span className="text-red-400 font-semibold">-{stats.totalSpent.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Spending Rate</span>
                      <span className={`font-semibold ${stats.spendingRate > 80 ? 'text-red-400' : 'text-green-400'}`}>
                        {stats.spendingRate.toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Avg Transaction</span>
                      <span className="text-blue-400 font-semibold">{stats.averageTransaction.toFixed(1)}</span>
                    </div>
                  </div>
                  
                  {isHeavySpender() && (
                    <div className="mt-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                      <div className="text-red-300 text-sm">
                        💡 You're a heavy spender! Consider upgrading to premium for unlimited access.
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Earning Suggestions */}
              {earningSuggestions.length > 0 && (
                <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-2xl p-6">
                  <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-blue-400" />
                    Earning Suggestions
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {earningSuggestions.map((suggestion, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-white/10 rounded-lg">
                        <Star className="w-4 h-4 text-yellow-400 mt-1 flex-shrink-0" />
                        <span className="text-gray-200 text-sm">{suggestion}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="bg-white/10 rounded-2xl p-6 border border-white/20">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <History className="w-5 h-5 text-blue-400" />
                  Transaction History
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {transactions.length === 0 ? (
                    <div className="text-center text-gray-400 py-8">
                      No transactions yet
                    </div>
                  ) : (
                    transactions.map((transaction) => (
                      <div key={transaction.id} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          transaction.type === 'earned' || transaction.type === 'bonus' || transaction.type === 'subscription'
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {transaction.type === 'earned' && <TrendingUp className="w-4 h-4" />}
                          {transaction.type === 'spent' && <Target className="w-4 h-4" />}
                          {transaction.type === 'bonus' && <Gift className="w-4 h-4" />}
                          {transaction.type === 'subscription' && <Crown className="w-4 h-4" />}
                        </div>
                        <div className="flex-1">
                          <div className="text-white font-medium">{transaction.description}</div>
                          <div className="text-gray-400 text-sm">
                            {new Date(transaction.timestamp).toLocaleDateString()}
                          </div>
                        </div>
                        <div className={`font-semibold ${
                          transaction.amount > 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Earn Coins Tab */}
          {activeTab === 'earn' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {earningMethods.map((method) => (
                  <div
                    key={method.id}
                    className={`p-6 rounded-2xl border transition-all duration-200 ${
                      method.highlight
                        ? 'bg-gradient-to-r from-purple-600/20 to-blue-600/20 border-purple-500/50'
                        : 'bg-white/10 border-white/20'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        method.highlight
                          ? 'bg-gradient-to-r from-purple-500 to-blue-500'
                          : 'bg-white/20'
                      }`}>
                        <method.icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold mb-2">{method.title}</h3>
                        <p className="text-gray-300 text-sm mb-3">{method.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-2xl font-bold text-green-400">+{method.coins}</span>
                          <button
                            onClick={method.action}
                            disabled={!method.available}
                            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                              method.available
                                ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600'
                                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            {method.available ? 'Get Coins' : 'Unavailable'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Features Tab */}
          {activeTab === 'features' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="bg-white/10 rounded-2xl p-6 border border-white/20">
                <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
                  <Target className="w-5 h-5 text-purple-400" />
                  Feature Costs & Access
                </h3>
                <div className="space-y-6">
                  {featureCategories.map((category) => (
                    <div key={category.category}>
                      <h4 className="text-lg font-medium text-purple-300 mb-3">{category.category}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {category.features.map((feature) => (
                          <div key={feature.name} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                            <div>
                              <div className="text-white font-medium">{feature.name}</div>
                              <div className="text-gray-400 text-sm">{feature.description}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-green-400">{feature.cost}</div>
                              <div className="text-xs text-gray-400">coins</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  )
}

export default RraasiCoins
